import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, Badge, Progress } from './components';
import {
  ArrowLeft,
  Phone,
  PhoneOff,
  Volume2,
  VolumeX,
  BarChart3,
  Target,
  MessageSquare,
  TrendingUp,
  Users,
  Loader2,
  Mic,
  MicOff
} from 'lucide-react';
import { useSompoAgentCall, LoadingState, Transcript } from './useSompoAgentCall';
import { cn } from '@/lib/utils';

interface SompoRoleplayProps {
  onBack: () => void;
}

interface FeedbackScore {
  category: string;
  score: number;
  feedback: string;
}

const SompoRoleplay: React.FC<SompoRoleplayProps> = ({ onBack }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isEndingCall, setIsEndingCall] = useState(false);

  const agentCallData = {
    agentId: 'jPVlBK7j8dPiyhY7ETHs',
    voiceId: 'aSXZu6bgEOS8MXVRzjPi',
    firstMessage: "Hello?",
    systemPrompt: `You are Andrew Tan, CFO of Pacific Shipping Co. on a phone call.

CRITICAL PHONE CALL BEHAVIOR:
- Start with just "Hello?" or "Hello, Andrew Tan speaking"
- Keep responses BRIEF and natural - 1-2 sentences max
- React like a real person on a phone call
- Sometimes interrupt if the caller is too long-winded
- Use natural phone fillers: "Uh-huh", "Right", "Okay", "I see"
- If you don't understand, say "Sorry, could you repeat that?" or "What was that?"
- Show impatience if appropriate: "Look, I have another call in 10 minutes..."

YOUR PERSONALITY:
- Busy CFO who values time
- Skeptical but not rude
- Wants facts, not fluff
- Will cut through sales talk quickly

KEY CONTEXT:
- Company: Pacific Shipping Co. (24 vessels, $125M revenue)
- Current coverage: Basic $50M Marine Liability
- Concerned about 15% Suez Canal tariff increase but not panicking
- Recently added 3 vessels and new Southeast Asia routes

NATURAL PHONE RESPONSES:
- "Yeah, what's this about?"
- "How much would that cost us?"
- "We've been fine so far."
- "Give me numbers, not theory."
- "I need to go soon, what's your point?"
- "Hmm, maybe. Send me details."
- "What are others paying for this?"

Remember: This is a PHONE CALL. Be brief, natural, sometimes impatient.`,
    maxCallDuration: 600,
  };

  const {
    loadingState,
    error,
    transcript,
    isCallActive,
    callDuration,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    isSpeaking
  } = useSompoAgentCall(agentCallData);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, isSpeaking]);

  const feedbackScores: FeedbackScore[] = [
    {
      category: 'Commercial Insight',
      score: 85,
      feedback: 'Referenced tariff impact effectively. Could tie in port-specific claim example next time.'
    },
    {
      category: 'Solution Framing',
      score: 90,
      feedback: 'Strong connection between tariff risk and marine cover. Good use of BI/FI hook.'
    },
    {
      category: 'Cross-Line Confidence',
      score: 75,
      feedback: "Mentioned aviation but didn't develop it fully. Room to deepen cross-sell fluency."
    },
    {
      category: 'Empathy & Tone',
      score: 95,
      feedback: 'Calm, professional, not pushy. Excellent executive tone.'
    },
    {
      category: 'Call-to-Action',
      score: 80,
      feedback: 'Secured next step, could be more specific (e.g., offer timeslot or named specialist).'
    }
  ];

  const handleCall = async () => {
    if (isCallActive) {
      setIsEndingCall(true);
      handleEndCall();
      // Show feedback after a delay
      setTimeout(() => {
        setIsEndingCall(false);
        setShowFeedback(true);
      }, 2000);
    } else {
      await handleAnswerCall();
    }
  };

  // Show loading spinner when ending call
  if (isEndingCall) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#fe4a0b] animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">Processing your session...</h3>
          <p className="text-gray-600 mt-2">Analyzing performance and generating feedback</p>
        </div>
      </div>
    );
  }

  if (showFeedback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  onClick={onBack}
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#fe4a0b] rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                  <h1 className="text-xl font-semibold text-gray-900">Practice Session Results</h1>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 border-0">
                Completed
              </Badge>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Overall Score */}
          <Card className="mb-8 border-0 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#fe4a0b] to-orange-600 p-8 text-white">
              <h2 className="text-2xl font-bold mb-2">Great Performance!</h2>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-bold">85%</span>
                <span className="text-xl opacity-90">Overall Score</span>
              </div>
            </div>
          </Card>

          {/* Detailed Feedback */}
          <Card className="mb-8 border-0 shadow-lg">
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#fe4a0b]" />
                Performance Breakdown
              </h3>

              <div className="space-y-6">
                {feedbackScores.map((item, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">{item.category}</h4>
                      <div className="flex items-center gap-3">
                        <Progress value={item.score} className="w-32 h-2" />
                        <span className="font-bold text-gray-900 w-12 text-right">{item.score}%</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 pl-0">{item.feedback}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Coaching Summary */}
          <Card className="mb-8 border-0 shadow-lg">
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-6 h-6 text-[#fe4a0b]" />
                Coaching Summary
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Great work leveraging the macro trigger (tariffs) to frame a wider risk advisory conversation.
                Consider expanding your explanation of Aviation relevance, and push a little more confidently
                toward the next step. Try the same pitch again, but from a warehousing risk angle instead.
              </p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={() => {
                setShowFeedback(false);
                window.location.reload();
              }}
              className="bg-[#fe4a0b] hover:bg-[#fe4a0b]/90 text-white font-medium flex-1"
            >
              Try Again
            </Button>
            <Button
              onClick={onBack}
              variant="outline"
              className="font-medium flex-1"
            >
              Back to Brief
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={onBack}
                variant="ghost"
                size="sm"
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#fe4a0b] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">S</span>
                </div>
                <h1 className="text-xl font-semibold text-gray-900">Practice Session</h1>
              </div>
            </div>
            <Badge className={cn(
              "border-0 transition-colors",
              isCallActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
            )}>
              {isCallActive ? `Call Active - ${formatTime(callDuration)}` : 'Ready to Start'}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Client Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Client Card */}
            <Card className="border-0 shadow-lg">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src="https://dopmo1eihgbgm.cloudfront.net/67be49a91a55ba49ef1c7c77/alex-45.webp"
                    alt="Andrew Tan"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">Andrew Tan</h3>
                    <p className="text-sm text-gray-600">CFO, Pacific Shipping Co.</p>
                    <Badge className="mt-2 bg-blue-100 text-blue-700 border-0 text-xs">
                      Logistics & Port Operations
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Current Coverage</span>
                    <span className="font-medium text-gray-900">$50M Marine</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Fleet Size</span>
                    <span className="font-medium text-gray-900">24 vessels</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Key Concern</span>
                    <span className="font-medium text-gray-900">Tariff Impact</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Context */}
            <Card className="border-0 shadow-lg">
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#fe4a0b]" />
                  Key Context
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full mt-1.5 flex-shrink-0" />
                    15% Suez Canal tariff increase
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full mt-1.5 flex-shrink-0" />
                    Mostly self-insured until now
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full mt-1.5 flex-shrink-0" />
                    Recently expanded fleet
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full mt-1.5 flex-shrink-0" />
                    New Southeast Asia routes
                  </li>
                </ul>
              </div>
            </Card>
          </div>

          {/* Right Panel - Roleplay Interface */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-lg h-full flex flex-col">
              {/* Call Controls */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Phone Call Roleplay</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Practice your pitch with Andrew Tan
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setIsMuted(!isMuted)}
                      disabled={!isCallActive}
                    >
                      {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={handleCall}
                      className={cn(
                        "gap-2 font-medium px-6",
                        isCallActive
                          ? "bg-red-500 hover:bg-red-600 text-white"
                          : "bg-[#fe4a0b] hover:bg-[#fe4a0b]/90 text-white"
                      )}
                      disabled={loadingState === LoadingState.CONNECTING}
                    >
                      {loadingState === LoadingState.CONNECTING ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Calling...
                        </>
                      ) : isCallActive ? (
                        <>
                          <PhoneOff className="w-4 h-4" />
                          End Call
                        </>
                      ) : (
                        <>
                          <Phone className="w-4 h-4" />
                          Start Call
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Conversation Area */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {!isCallActive && loadingState === LoadingState.INITIAL ? (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-[#fe4a0b]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Phone className="w-10 h-10 text-[#fe4a0b]" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Ready to Practice</h4>
                      <p className="text-gray-600 max-w-sm mx-auto">
                        Click "Start Call" to begin your phone roleplay with Andrew Tan
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Connection Status */}
                    {loadingState === LoadingState.CONNECTING && (
                      <div className="p-6">
                        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
                          <Phone className="w-5 h-5 text-blue-600 animate-pulse" />
                          <p className="text-blue-700">Calling Andrew Tan...</p>
                        </div>
                      </div>
                    )}

                    {/* Chat Messages */}
                    <div
                      ref={scrollRef}
                      className="flex-1 overflow-y-auto p-6 space-y-4"
                    >
                      {transcript.map((entry, index) => (
                        <div
                          key={index}
                          className={cn(
                            "flex",
                            entry.source === 'agent' ? "justify-start" : "justify-end"
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[70%] rounded-xl px-4 py-2",
                              entry.source === 'agent'
                                ? "bg-gray-100 text-gray-900"
                                : "bg-[#fe4a0b] text-white"
                            )}
                          >
                            <p className="text-sm">{entry.message}</p>
                            <p className={cn(
                              "text-xs mt-1",
                              entry.source === 'agent' ? "text-gray-500" : "text-white/70"
                            )}>
                              {entry.timestamp.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Speaking Indicator */}
                      {isSpeaking && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-xl px-4 py-3">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Voice Input Indicator */}
                    {isCallActive && (
                      <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-center gap-3">
                          <Mic className={cn(
                            "w-5 h-5",
                            isMuted ? "text-gray-400" : "text-[#fe4a0b]"
                          )} />
                          <p className="text-sm text-gray-600">
                            {isMuted ? "Microphone muted" : "Speak naturally - Andrew is listening"}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {error && (
                  <div className="p-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SompoRoleplay;